import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useCreatePromptTemplate, useCreatePromptVersion } from "@/queries";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";
import { formatBlueprintForAPI } from "@/components/Playground/utils";
import { ENDPOINTS } from "@/api/application-api";
import { PromptVersion } from "@/types/apiGetters";
import { PromptBlueprint } from "@/types";

interface UsePromptOperationsProps {
  userToken: string;
  workspaceId: number;
  mode: "create" | "edit";
  existingPromptId?: number;
  existingPromptName?: string;
  parentVersionId?: number;
  onSaveComplete: (result: { id: number; name: string; version?: number }) => void;
  onClose: () => void;
}

interface UsePromptOperationsReturn {
  isSaving: boolean;
  submittedVersion: PromptVersion | undefined;
  showCommitDialog: boolean;
  setShowCommitDialog: (show: boolean) => void;
  handleSaveCreate: (promptName: string, blueprint: PromptBlueprint) => Promise<void>;
  handleSaveEdit: (commitMessage: string | undefined, blueprint: PromptBlueprint) => void;
  handleSave: (promptName: string, blueprint: PromptBlueprint) => void;
}

export const usePromptOperations = ({
  userToken,
  workspaceId,
  mode,
  existingPromptId,
  existingPromptName: _existingPromptName,
  parentVersionId,
  onSaveComplete,
  onClose,
}: UsePromptOperationsProps): UsePromptOperationsReturn => {
  const [isSaving, setIsSaving] = useState(false);
  const [submittedVersion, setSubmittedVersion] = useState<PromptVersion | undefined>();
  const [showCommitDialog, setShowCommitDialog] = useState(false);

  const queryClient = useQueryClient();
  const createPromptMutation = useCreatePromptTemplate(userToken);
  const createVersionMutation = useCreatePromptVersion(userToken);

  const invalidateQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.prompt_registry_objects],
      }),
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.prompt_templates],
      }),
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.get_prompt],
      }),
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.parse_input_variables],
      }),
    ]);
  }, [queryClient]);

  const handleSaveCreate = useCallback(
    async (promptName: string, blueprint: PromptBlueprint) => {
      if (!promptName) {
        displayErrorToast("Please provide a name for the prompt");
        return;
      }

      setIsSaving(true);
      const formattedBlueprint = formatBlueprintForAPI(blueprint);

      try {
        await new Promise<void>((res, reject) => {
          createPromptMutation.mutate(
            {
              prompt_template: {
                prompt_name: promptName,
                workspace_id: workspaceId,
              },
              prompt_version: formattedBlueprint,
            },
            {
              onSuccess: async responseData => {
                if ("success" in responseData && !responseData.success) {
                  displayErrorToast(responseData.message || "Failed to create prompt");
                  reject(new Error(responseData.message || "Failed to create prompt"));
                  return;
                }

                displayToast("Prompt created successfully", ToastType.success);

                // Invalidate queries and wait for completion
                await invalidateQueries();

                // Wait a moment for data to propagate
                // eslint-disable-next-line no-promise-executor-return
                await new Promise(resolvePromise => setTimeout(resolvePromise, 100));

                onSaveComplete({
                  id: responseData.prompt_version.prompt_id,
                  name: promptName,
                  version: responseData.prompt_version.number || 1,
                });

                onClose();
                res();
              },
              onError: error => {
                displayErrorToast((error as Error)?.message || "Failed to create prompt");
                reject(error);
              },
              onSettled: () => {
                setIsSaving(false);
              },
            },
          );
        });
      } catch (error) {
        setIsSaving(false);
        throw error;
      }
    },
    [workspaceId, createPromptMutation, invalidateQueries, onSaveComplete, onClose],
  );

  const handleSaveEdit = useCallback(
    (commitMessage: string | undefined, blueprint: PromptBlueprint) => {
      if (!existingPromptId) {
        displayErrorToast("No prompt selected for editing");
        return;
      }

      setIsSaving(true);
      const formattedBlueprint = formatBlueprintForAPI(blueprint);

      createVersionMutation.mutate(
        {
          ...formattedBlueprint,
          prompt_id: existingPromptId,
          commit_message: commitMessage || "Updated via Workflow Builder",
          parent_version_id: parentVersionId,
        },
        {
          onSuccess: async responseData => {
            if ("success" in responseData && !responseData.success) {
              displayErrorToast(responseData.message || "Failed to save new version");
              setIsSaving(false);
              return;
            }

            setSubmittedVersion(responseData as PromptVersion);
            displayToast("New version saved successfully", ToastType.success);

            // Invalidate queries
            await invalidateQueries();

            setIsSaving(false);
          },
          onError: error => {
            displayErrorToast((error as Error)?.message || "Failed to save new version");
            setIsSaving(false);
          },
        },
      );
    },
    [existingPromptId, parentVersionId, createVersionMutation, invalidateQueries],
  );

  const handleSave = useCallback(
    (promptName: string, blueprint: PromptBlueprint) => {
      if (mode === "create") {
        handleSaveCreate(promptName, blueprint);
      } else {
        setSubmittedVersion(undefined);
        setShowCommitDialog(true);
      }
    },
    [mode, handleSaveCreate],
  );

  return {
    isSaving,
    submittedVersion,
    showCommitDialog,
    setShowCommitDialog,
    handleSaveCreate,
    handleSaveEdit,
    handleSave,
  };
};
